import { useState } from 'react';
import { useMutation } from '@apollo/client';
import { captureException } from '@sentry/react';
import { INSERT_MANIFEST } from '@/components/Manifests/graphql';
import { INSERT_SAVED_CUSTOMER_LOCATION } from '../../../graphql/mutations';
import { useRecalcStopServiceTimes } from '@/hooks';
import { post } from '@/utilities/onwardClient';
import { NOTIFY_CARRIER_OF_ADMIN_ASSIGNMENT } from '@/constants/apiRoutes';
import { serviceLinesChanged } from '@/utilities/serviceStopRecalc';
import { price, distance } from '../../../utilities/processOrders';
import { buildSavePayload, savedCustomerRow } from '../utils/savePayload';

const reportError = (error) => {
    console.error(error.message);
    captureException(error);
};

/**
 * The Done sequence of the Edit Order modal, top to bottom: distance and price, the stale-save
 * check, the payload, `callbacks.onSave`, then the side effects that follow a save.
 *
 * `save()` runs in this order, and a blocked guard stops it before anything below the check:
 *  1. distance and price the clone
 *  2. `guard.checkBeforeSave()` (stop on `ok: false`)
 *  3. `buildSavePayload`, then reset the note and insert the saved customer
 *  4. `callbacks.onSave`, then `guard.markSaved()`
 *  5. recalc stop service times if service lines changed, insert the single-order manifest,
 *     email the newly assigned carrier
 *
 * The version guard needs `saveInflight` to hide its prompt during a save, and this hook needs the
 * guard to run the check. To break that loop the guard is passed as a ref the shell fills in after
 * calling the guard hook; `save()` only runs from an event handler, after that render has finished.
 *
 * The manifest, carrier-email, saved-customer, and note flags stay in the shell and arrive here as
 * values and setters.
 *
 * @param {object} args
 * @param {object} args.order The order prop.
 * @param {object} args.draft Return value of `useOrderDraft` (`clone`, `baseOrder`).
 * @param {{ current: object|null }} args.guardRef Ref holding the return value of `useOrderVersionGuard`.
 * @param {object} args.callbacks The caller's callbacks (`onSave`, `onError`).
 * @param {boolean} args.pcMilerApplies True when distance routes through PC Miler.
 * @param {object} args.errors Field errors from the modal, sent to `onSave` as `errors`.
 * @param {boolean} args.isNewOrder True for an order that is not saved yet.
 * @param {{ roles?: object, user?: { user_id: string }, user_id: string, client_id: string, userType: string }} args.identity
 *   Who is saving, for the new note and the saved customer.
 * @param {{ noteInfo: object, resetNote: () => void }} args.note The Notes tab draft and how to clear it.
 * @param {{ checked: boolean, clear: () => void }} args.saveCustomer The Save Customer box and how to untick it.
 * @param {{ manifest: object|null, clear: () => void }} args.singleOrderManifest A manifest to insert after the save.
 * @param {{ pending: boolean, clear: () => void }} args.carrierAssignedEmail Whether to email the new carrier.
 * @returns {{ save: () => Promise<void>, saveInflight: boolean }}
 *   `save` is what the Done button calls. `saveInflight` is true from the click until the sequence ends.
 */
export const useSaveOrder = ({
    order,
    draft,
    guardRef,
    callbacks,
    pcMilerApplies,
    errors,
    isNewOrder,
    identity,
    note,
    saveCustomer,
    singleOrderManifest,
    carrierAssignedEmail,
}) => {
    const [saveInflight, setSaveInflight] = useState(false);
    const [insertManifest] = useMutation(INSERT_MANIFEST, { onError: reportError });
    const [insertSavedCustomer] = useMutation(INSERT_SAVED_CUSTOMER_LOCATION, { onError: reportError });
    // Nulls the containing stops' service_time + flags routes for re-optimize when services change.
    const recalcStopServiceTimes = useRecalcStopServiceTimes(order);

    const save = async () => {
        const { clone, baseOrder } = draft;
        const guard = guardRef.current;
        const isAdmin = !!identity.roles?.['ADMIN'];

        setSaveInflight(true);
        // Always re-run distance + price on save. `disableGeocoding` only tells the
        // autocomplete/map not to re-geocode; it does NOT mean "skip pricing". distance() and
        // price() run on the order's existing lat/lng, so neither conflicts with a manual pin.
        const [, [distancedOrder]] = await distance([clone], { pcMiler: { applies: pcMilerApplies } });
        const [failed, [priced]] = await price([distancedOrder]);
        const failedPricing = failed[order.key || order.order_id];

        // Stale-save guard. Runs before any side effect (note reset, saved customer, onSave) so a
        // blocked save leaves the modal untouched.
        const check = await guard.checkBeforeSave();
        if (!check.ok) {
            setSaveInflight(false);
            return;
        }

        const payload = buildSavePayload({
            priced,
            clone,
            baseOrder,
            stored: check.stored,
            noteInfo: note.noteInfo,
            author: {
                isAdmin,
                // Only a note reads the author id, so an admin without one never touches `user`.
                user_id: isAdmin ? (note.noteInfo.note ? identity.user.user_id : null) : identity.user_id,
                client_id: identity.client_id,
                userType: identity.userType,
                isNewOrder,
            },
        });
        if (note.noteInfo.note) {
            note.resetNote();
        }

        if (saveCustomer.checked) {
            insertSavedCustomer({ variables: { customer: savedCustomerRow(clone, identity.client_id) } });
            saveCustomer.clear();
        }

        await callbacks.onSave({
            errors: {
                ...errors,
                hasMissingField: [],
                failedPricing,
            },
            ...payload,
        });
        guard.markSaved();

        // If services changed, null stop service time and flag routes to re-optimize. The order is
        // already saved at this point, so a failure here means the stop keeps its stale
        // service_time. Surface it instead of failing quietly.
        if (
            serviceLinesChanged({
                original: baseOrder.services,
                modified: payload.services,
                removed: payload.servicesRemoved,
            })
        ) {
            const recalcOk = await recalcStopServiceTimes();
            if (!recalcOk) {
                callbacks.onError?.(
                    'Services were saved, but the route stop times could not be refreshed. Please re-optimize the route.'
                );
            }
        }

        if (singleOrderManifest.manifest) {
            await insertManifest({ variables: { manifest: singleOrderManifest.manifest } });
            singleOrderManifest.clear();
        }

        if (carrierAssignedEmail.pending && payload.order.carrier_id !== baseOrder.carrier_id) {
            await post(NOTIFY_CARRIER_OF_ADMIN_ASSIGNMENT, {
                order,
                new_carrier_id: payload.order.carrier_id,
            });
            carrierAssignedEmail.clear();
        }

        setSaveInflight(false);
    };

    return { save, saveInflight };
};
